import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning';
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = 'danger',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t('common.delete');
  const resolvedCancelLabel = cancelLabel ?? t('common.cancel');
  const [isClosing, setIsClosing] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) setIsClosing(false);
  }, [open]);

  const handleCancel = () => {
    setIsClosing(true);
    setTimeout(onCancel, 150);
  };

  const handleConfirm = () => {
    setIsClosing(true);
    setTimeout(onConfirm, 150);
  };

  if (!open) return null;

  const colors = variant === 'danger'
    ? { bg: 'bg-red-100', icon: 'text-red-600', btn: 'bg-red-600 hover:bg-red-700 focus:ring-red-500' }
    : { bg: 'bg-orange-100', icon: 'text-orange-600', btn: 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500' };

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-150 ${isClosing ? 'opacity-0' : 'opacity-100'}`}
    >
      <div className="fixed inset-0 bg-black/40" onClick={handleCancel} />
      <div
        ref={dialogRef}
        className={`relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 transition-transform duration-150 ${isClosing ? 'scale-95' : 'scale-100'}`}
      >
        <div className="flex items-start gap-4">
          <div className={`shrink-0 w-10 h-10 rounded-full ${colors.bg} flex items-center justify-center`}>
            <span className={`text-lg ${colors.icon}`}>⚠️</span>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="mt-2 text-sm text-gray-600">{message}</p>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            {resolvedCancelLabel}
          </button>
          <button
            onClick={handleConfirm}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${colors.btn}`}
          >
            {resolvedConfirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
